/**
 * JewelleryBatchPrintDialog — print the tags of up to 30 jewellery pieces in
 * one go.
 *
 * Selection comes from two places (both feed the same list):
 *   • checkboxes on the Jewellery grid cards (passed in as `initialSelectedIds`)
 *   • a searchable multi-select dropdown inside this dialog
 * plus a "Select all (current filter)" shortcut that respects whatever
 * received-date / goldsmith / search filters are active on the page (the
 * caller passes the already-filtered `items`).
 *
 * Output (applied to the whole batch):
 *   • Full Tag  — complete tag card, auto layout per item (reuses the exact
 *                 single-tag renderer), honouring the Raw-material and Pieces
 *                 toggles.
 *   • QR only   — a QR sticker + jewellery number.
 *   • QR + Image — QR and photo side by side.
 * QR size (XS / S / M / L) applies to the two QR modes.
 *
 * All items flow onto an A4 sheet (multiple per page) with page-break-safe
 * wrappers so a tag is never split across pages.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { api } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Printer, QrCode, Image as ImageIcon, FileText, Search, X, ChevronsUpDown,
  CheckCheck, Loader2, Download, Eye, EyeOff, ArrowDownUp, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
import {
  BatchTagItem, TagMeasureNode, buildTagDataFromDetail, computeDefaultLayout,
  QR_LABEL_SIZES, TAG_SIZES, sizeFactor, openPrintWindow,
} from '@/components/jewellery/JewelleryTagViewer';
import { mmToPx, chooseFit, FIT_CANDIDATES, FIT_MAX } from '@/lib/tagFit';

export const BATCH_TAG_LIMIT = 30;

// Per-tag layout choices offered in batch (Auto = best-fit per piece).
const BATCH_LAYOUT_OPTIONS = [
  { id: 'auto', label: 'Auto' },
  { id: 'classic', label: 'Classic' },
  { id: 'grid2', label: '2-Col' },
  { id: 'grid3', label: '3-Col' },
];

const OUTPUT_TYPES = [
  { id: 'full', label: 'Full Tag', icon: FileText, desc: 'Complete tag, auto layout' },
  { id: 'qr', label: 'QR only', icon: QrCode, desc: 'QR + number sticker' },
  { id: 'qrimg', label: 'QR + Image', icon: ImageIcon, desc: 'QR beside the photo' },
];

const SORT_OPTIONS = [
  { id: 'manual', label: 'Selection order' },
  { id: 'received', label: 'Received date' },
  { id: 'jewno', label: 'Jewellery no' },
];

export function JewelleryBatchPrintDialog({
  open,
  onOpenChange,
  companyId,
  items = [],
  initialSelectedIds = [],
  apiClient = api,
  batchEndpoint = '/jewellery/tags/batch',
}) {
  const [ids, setIds] = useState([]);
  const [mode, setMode] = useState('full');
  const [qrSizeId, setQrSizeId] = useState('sm');
  const [sizeId, setSizeId] = useState('lg'); // tag SIZE preset (Full Tag)
  const [customOn, setCustomOn] = useState(false); // custom mm size mode
  const [customW, setCustomW] = useState(40); // label width in mm
  const [customH, setCustomH] = useState(25); // label height in mm
  const [measuredById, setMeasuredById] = useState({}); // { [id]: [{layoutId,purityPos,w,h}] }
  const [effortMode, setEffortMode] = useState(null); // null | 'skip' | 'best' (custom non-fit handling)
  const [warnOpen, setWarnOpen] = useState(false); // fit warning dialog
  const [layoutOverrideById, setLayoutOverrideById] = useState({}); // { [id]: 'classic'|'grid2'|'grid3' }
  const [rmMode, setRmMode] = useState('expanded'); // 'expanded' | 'collapsed'
  const [showPieces, setShowPieces] = useState(true);
  const [showTagQr, setShowTagQr] = useState(false); // QR on the Full Tag (default OFF)
  const [sortKey, setSortKey] = useState('manual'); // manual | received | jewno
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState('');
  const [batchData, setBatchData] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [preview, setPreview] = useState(false);
  const [actionSeq, setActionSeq] = useState(0);

  const offscreenRef = useRef(null);
  const actionRef = useRef(null);          // 'print' | 'pdf' | 'preview'
  const batchDataKeyRef = useRef('');       // id-set the current batchData was fetched for
  const measureRefs = useRef({});           // { `${id}::${candidateIndex}`: node } for fit measurement
  const pendingActionRef = useRef(null);    // action awaiting the fit-warning choice

  // Sync selection + reset transient state whenever the dialog opens.
  useEffect(() => {
    if (open) {
      setIds((initialSelectedIds || []).slice(0, BATCH_TAG_LIMIT));
      setBatchData([]);
      batchDataKeyRef.current = '';
      setBusy(false);
      setBusyLabel('');
      setQuery('');
      setPreview(false);
      setLayoutOverrideById({});
      setCustomOn(false);
      setCustomW(40);
      setCustomH(25);
      setMeasuredById({});
      setEffortMode(null);
      setWarnOpen(false);
      measureRefs.current = {};
      pendingActionRef.current = null;
      actionRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Resolve the layout for a given tag: a manual per-tag override wins,
  // otherwise the automatic best-fit per piece.
  const resolveLayout = (d) => {
    const ov = layoutOverrideById[d.id];
    if (ov && ov !== 'auto') return ov;
    return computeDefaultLayout(d, rmMode);
  };

  const qrPx = useMemo(
    () => (QR_LABEL_SIZES.find((q) => q.id === qrSizeId) || QR_LABEL_SIZES[0]).px,
    [qrSizeId],
  );

  const labelById = useMemo(() => {
    const m = new Map();
    for (const it of items) m.set(it.id, it);
    return m;
  }, [items]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => (
      (it.jewellery_no || '').toLowerCase().includes(q)
      || (it.title || '').toLowerCase().includes(q)
      || (it.jewellery_type || '').toLowerCase().includes(q)
    ));
  }, [items, query]);

  const atLimit = ids.length >= BATCH_TAG_LIMIT;

  // The print/preview/PDF order: honour the chosen sort, falling back to the
  // raw selection order. Applied client-side on the already-fetched data.
  const orderedBatch = useMemo(() => {
    const arr = [...batchData];
    if (sortKey === 'jewno') {
      arr.sort((a, b) => String(a.jewelleryNo || '').localeCompare(String(b.jewelleryNo || ''), undefined, { numeric: true, sensitivity: 'base' }));
    } else if (sortKey === 'received') {
      arr.sort((a, b) => String(labelById.get(a.id)?.received_at || '').localeCompare(String(labelById.get(b.id)?.received_at || '')));
    }
    return arr;
  }, [batchData, sortKey, labelById]);

  // ── Custom exact-size (mm) fit engine ─────────────────────────────────────
  // Active only for Full Tag + Custom mode. For each selected tag we measure
  // every layout candidate at natural size (off-screen) and let chooseFit pick
  // the layout + scale that best fills the exact label box. Auto-fit here wins
  // over any manual per-tag layout override.
  const customActive = mode === 'full' && customOn;
  const customWpx = mmToPx(customW);
  const customHpx = mmToPx(customH);

  // Eagerly load the batch payload when custom mode is on so the fit can be
  // measured and shown before the user prints.
  useEffect(() => {
    if (!open || !customActive || !ids.length) return;
    (async () => { try { await ensureBatchData(); } catch (_) { /* surfaced on action */ } })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customActive, ids]);

  // Measure the natural (s=1) size of each fit candidate for every tag.
  useEffect(() => {
    if (!customActive || !orderedBatch.length) return;
    const map = {};
    for (const d of orderedBatch) {
      map[d.id] = FIT_CANDIDATES.map((c, i) => {
        const node = measureRefs.current[`${d.id}::${i}`];
        if (!node) return { ...c, w: 0, h: 0 };
        const r = node.getBoundingClientRect();
        return { ...c, w: r.width, h: r.height };
      });
    }
    setMeasuredById(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customActive, orderedBatch, rmMode, showPieces, showTagQr]);

  // Per-tag fit result from the measured naturals + the current box size.
  const fitById = useMemo(() => {
    if (!customActive) return {};
    const out = {};
    for (const d of orderedBatch) {
      const measured = measuredById[d.id];
      if (!measured) continue;
      out[d.id] = chooseFit(measured, customWpx, customHpx);
    }
    return out;
  }, [customActive, orderedBatch, measuredById, customWpx, customHpx]);

  const nonFitIds = useMemo(
    () => (customActive ? orderedBatch.filter((d) => fitById[d.id] && !fitById[d.id].fits).map((d) => d.id) : []),
    [customActive, orderedBatch, fitById],
  );

  // Any change to the custom box / content invalidates a prior skip/best choice.
  useEffect(() => {
    setEffortMode(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customOn, customW, customH, rmMode, showPieces, showTagQr, ids]);

  // The tags that actually get printed/exported for the current choice: in
  // "skip" mode the non-fitting ones are dropped; otherwise all are kept.
  const renderList = useMemo(() => {
    if (!customActive) return orderedBatch;
    if (effortMode === 'skip') return orderedBatch.filter((d) => fitById[d.id]?.fits);
    return orderedBatch;
  }, [customActive, orderedBatch, effortMode, fitById]);

  // Render one batch item, honouring custom-fit layout/scale when active.
  // `forPreview` shows non-fitting tags clipped (with an amber box) so the
  // problem is visible before the user resolves the warning dialog.
  const renderBatchItem = (d, forPreview = false) => {
    if (customActive) {
      const f = fitById[d.id];
      const fits = !!(f && f.fits);
      let s;
      if (!f) s = sizeFactor(sizeId);
      else if (fits) s = f.scale;
      else {
        const effort = forPreview ? effortMode : effortMode; // same resolution
        s = effort === 'best' ? Math.min(f.sRaw || 0.1, FIT_MAX) : f.scale; // skip/clip => floor (clipped)
      }
      return (
        <BatchTagItem
          key={`${forPreview ? 'pv-' : ''}${d.id}`}
          data={d}
          mode="full"
          layoutId={f?.layoutId || 'classic'}
          purityPos={f?.purityPos || 'header'}
          scaleOverride={s}
          boxW={customWpx}
          boxH={customHpx}
          boxWarn={!fits}
          rmMode={rmMode}
          showPieces={showPieces}
          showQr={showTagQr}
          qrPx={qrPx}
        />
      );
    }
    return (
      <BatchTagItem
        key={`${forPreview ? 'pv-' : ''}${d.id}`}
        data={d}
        mode={mode}
        layoutId={mode === 'full' ? resolveLayout(d) : undefined}
        rmMode={rmMode}
        showPieces={showPieces}
        showQr={showTagQr}
        qrPx={qrPx}
        sizeId={sizeId}
      />
    );
  };

  const toggleId = (id) => {
    setIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= BATCH_TAG_LIMIT) {
        toast.error(`You can print up to ${BATCH_TAG_LIMIT} tags at once.`);
        return prev;
      }
      return [...prev, id];
    });
  };

  const selectAllFiltered = () => {
    const all = items.map((it) => it.id);
    const capped = all.slice(0, BATCH_TAG_LIMIT);
    setIds(capped);
    if (all.length > BATCH_TAG_LIMIT) {
      toast.warning(`Selected the first ${BATCH_TAG_LIMIT} of ${all.length} filtered pieces (batch limit).`);
    }
  };

  const clearAll = () => { setIds([]); setPreview(false); };

  // Fetch the batch payload if the current id-set differs from what we already
  // have. Returns the freshly-built (or cached) tag-data array.
  const ensureBatchData = async () => {
    const key = [...ids].sort().join(',');
    if (key === batchDataKeyRef.current && batchData.length) return batchData;
    const res = await apiClient.post(batchEndpoint, {
      company_id: companyId,
      jewellery_ids: ids,
    });
    const raw = res?.data?.items || [];
    const missing = res?.data?.missing_ids || [];
    if (!raw.length) throw new Error('None of the selected pieces could be loaded.');
    const built = raw.map(buildTagDataFromDetail);
    batchDataKeyRef.current = key;
    setBatchData(built);
    if (missing.length) {
      toast.warning(`${missing.length} piece(s) could not be found and were skipped.`);
    }
    return built;
  };

  // Load data, then schedule the action to run once the off-screen sheet
  // reflects the latest data + options.
  const proceed = async (action) => {
    setBusy(true);
    setBusyLabel(action === 'pdf' ? 'Building PDF…' : action === 'preview' ? 'Loading…' : 'Preparing…');
    try {
      await ensureBatchData();
      actionRef.current = action;
      setActionSeq((s) => s + 1);
    } catch (e) {
      toast.error(extractError(e, 'Failed to prepare the batch.'));
      setBusy(false);
      setBusyLabel('');
    }
  };

  // Guard against invalid selection. For a custom size with tags that do not
  // fit, intercept Print / PDF to make the user choose Skip vs Best-effort.
  const runAction = async (action) => {
    if (!ids.length) { toast.error('Select at least one piece.'); return; }
    if (ids.length > BATCH_TAG_LIMIT) { toast.error(`You can print up to ${BATCH_TAG_LIMIT} tags at once.`); return; }
    if (customActive && (action === 'print' || action === 'pdf') && nonFitIds.length > 0 && !effortMode) {
      pendingActionRef.current = action;
      setWarnOpen(true);
      return;
    }
    proceed(action);
  };

  // Resolve the fit-warning dialog. 'skip' drops the non-fitting tags for this
  // action; 'best' prints everything, shrinking non-fitting tags to fit the box.
  const resolveWarn = (choice) => {
    if (choice === 'skip' && (orderedBatch.length - nonFitIds.length) === 0) {
      toast.error(`No tag fits in ${customW}×${customH} mm. Increase the size or choose best-effort.`);
      setWarnOpen(false);
      pendingActionRef.current = null;
      return;
    }
    setEffortMode(choice);
    setWarnOpen(false);
    const action = pendingActionRef.current || 'print';
    pendingActionRef.current = null;
    // Defer so renderList reflects the new effortMode before we rasterise.
    setTimeout(() => proceed(action), 0);
  };

  // Rasterise each tag node and pack them into A4 pages (row by row), never
  // splitting a tag across a page — the PDF mirrors the printed sheet and is
  // self-contained so it prints identically on any printer.
  const exportPdf = async (containerEl, count) => {
    const nodes = Array.from(containerEl.children);
    const pdf = new jsPDF({ unit: 'mm', format: 'a4', compress: true });
    const PX2MM = 25.4 / 96;
    const margin = 8;
    const contentW = 210 - margin * 2;
    const contentH = 297 - margin * 2;
    const gap = 3;
    let x = 0; let y = 0; let rowH = 0; let placed = 0;
    for (const node of nodes) {
      // eslint-disable-next-line no-await-in-loop
      const canvas = await html2canvas(node, { scale: 2, backgroundColor: '#ffffff', logging: false, useCORS: true });
      const wMM = (canvas.width / 2) * PX2MM;
      const hMM = (canvas.height / 2) * PX2MM;
      if (placed > 0 && x + wMM > contentW + 0.1) { x = 0; y += rowH + gap; rowH = 0; }
      if (y + hMM > contentH + 0.1 && y > 0) { pdf.addPage(); x = 0; y = 0; rowH = 0; }
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', margin + x, margin + y, wMM, hMM);
      x += wMM + gap;
      rowH = Math.max(rowH, hMM);
      placed += 1;
    }
    pdf.save(`Jewellery_Tags_${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success(`Downloaded PDF with ${count} tag(s).`);
  };

  // Runs the scheduled action after the off-screen sheet has painted. Two RAFs
  // let the QR SVGs + images lay out before we snapshot / rasterise.
  useEffect(() => {
    if (!actionSeq) return undefined;
    const action = actionRef.current;
    if (!action) return undefined;
    const el = offscreenRef.current;
    if (!el) return undefined;
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(async () => {
        try {
          const n = el.children.length;
          if (!n) {
            toast.error('Nothing to print for the current size and selection.');
            return;
          }
          if (action === 'print') {
            openPrintWindow(el.innerHTML, {
              pageSize: 'A4', margin: '8mm',
              title: `Jewellery Tags (${n})`,
            });
            toast.success(`Prepared ${n} tag(s) for printing.`);
          } else if (action === 'pdf') {
            await exportPdf(el, n);
          } else if (action === 'preview') {
            setPreview(true);
          }
        } catch (err) {
          toast.error(action === 'pdf' ? 'Could not build the PDF.' : 'Could not open the print window. Please allow pop-ups.');
        } finally {
          actionRef.current = null;
          setBusy(false);
          setBusyLabel('');
        }
      });
    });
    return () => { cancelAnimationFrame(raf1); if (raf2) cancelAnimationFrame(raf2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionSeq]);

  // If the selection changes while a preview is showing, refresh it.
  useEffect(() => {
    if (!preview) return;
    const key = [...ids].sort().join(',');
    if (key !== batchDataKeyRef.current) {
      if (!ids.length) { setPreview(false); return; }
      runAction('preview');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  const isQrMode = mode === 'qr' || mode === 'qrimg';

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange?.(o); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="jewellery-batch-print-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer size={18} className="text-[var(--jewellery-icon)]" />
            Batch Print Tags
          </DialogTitle>
          <DialogDescription>
            Pick up to {BATCH_TAG_LIMIT} pieces, choose what to print, then print them all on an A4 sheet.
            Scanning any QR opens the public tag. No value or price is ever shown.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-1">
          {/* ── Selection ─────────────────────────────────────────── */}
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Pieces to print</span>
              <Badge
                variant="outline"
                className={`font-mono text-[11px] ${atLimit ? 'border-amber-300 text-amber-700 bg-amber-50' : ''}`}
                data-testid="batch-selected-count"
              >
                {ids.length} / {BATCH_TAG_LIMIT}
              </Badge>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={pickerOpen}
                    className="h-9 justify-between font-normal flex-1 min-w-[220px] text-slate-600"
                    data-testid="batch-multiselect-trigger"
                  >
                    <span className="flex items-center gap-2">
                      <Search size={14} className="text-slate-400" />
                      Add / remove pieces…
                    </span>
                    <ChevronsUpDown size={14} className="text-slate-400" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] max-w-[520px]" align="start">
                  <div className="border-b border-[var(--divider)] p-2">
                    <Input
                      autoFocus
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search by number, title or type…"
                      className="h-8 text-sm"
                      data-testid="batch-multiselect-search"
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto py-1">
                    {filteredOptions.length === 0 ? (
                      <div className="px-3 py-4 text-center text-xs text-slate-500" data-testid="batch-multiselect-empty">
                        {items.length === 0 ? 'No jewellery available.' : 'No matches.'}
                      </div>
                    ) : (
                      filteredOptions.map((it) => {
                        const checked = ids.includes(it.id);
                        const disabled = !checked && atLimit;
                        return (
                          <button
                            key={it.id}
                            type="button"
                            onClick={() => toggleId(it.id)}
                            disabled={disabled}
                            className={[
                              'w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors',
                              disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50',
                              checked ? 'bg-[var(--jewellery-bg)]/40' : '',
                            ].join(' ')}
                            data-testid={`batch-option-${it.id}`}
                          >
                            <Checkbox checked={checked} className="pointer-events-none" tabIndex={-1} />
                            <span className="min-w-0 flex-1">
                              <span className="font-mono text-[12px] text-emerald-700">{it.jewellery_no}</span>
                              {it.title ? <span className="text-slate-600"> · {it.title}</span> : null}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={selectAllFiltered}
                className="h-9"
                disabled={items.length === 0}
                data-testid="batch-select-all"
                title="Select all pieces in the current page filter (up to the limit)"
              >
                <CheckCheck size={14} className="mr-1.5" /> Select all (filtered)
              </Button>
              {ids.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearAll}
                  className="h-9 text-slate-500"
                  data-testid="batch-clear"
                >
                  <X size={13} className="mr-1" /> Clear
                </Button>
              )}
            </div>

            {/* Selected chips */}
            {ids.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 rounded-md border border-slate-200 bg-slate-50/60 p-2 max-h-28 overflow-y-auto" data-testid="batch-selected-chips">
                {ids.map((id) => {
                  const it = labelById.get(id);
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 rounded-full border border-[var(--jewellery-border)] bg-white pl-2 pr-1 py-0.5 text-[11px] font-mono text-[var(--ink-navy)]"
                      data-testid={`batch-chip-${id}`}
                    >
                      {it?.jewellery_no || id.slice(0, 6)}
                      <button
                        type="button"
                        onClick={() => toggleId(id)}
                        className="inline-flex items-center justify-center w-4 h-4 rounded-full text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                        aria-label={`Remove ${it?.jewellery_no || id}`}
                        data-testid={`batch-chip-remove-${id}`}
                      >
                        <X size={11} />
                      </button>
                    </span>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-slate-200 p-3 text-center text-[11px] text-slate-400">
                Nothing selected yet. Use the dropdown above, "Select all (filtered)", or tick cards on the Jewellery page.
              </div>
            )}
          </section>

          {/* ── Output type ─────────────────────────────────────────── */}
          <section className="space-y-2">
            <span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">What to print</span>
            <div className="grid grid-cols-3 gap-2" data-testid="batch-output-type">
              {OUTPUT_TYPES.map((t) => {
                const active = mode === t.id;
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setMode(t.id)}
                    aria-pressed={active}
                    data-testid={`batch-output-${t.id}`}
                    className={[
                      'rounded-md border px-3 py-2.5 text-left transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--jewellery-icon)]',
                      active
                        ? 'border-[var(--jewellery-icon)] bg-[var(--jewellery-bg)]'
                        : 'border-slate-200 bg-white hover:border-[var(--jewellery-border)] hover:bg-slate-50',
                    ].join(' ')}
                  >
                    <div className={`flex items-center gap-1.5 text-[12px] font-medium ${active ? 'text-[var(--jewellery-icon)]' : 'text-[var(--ink-navy)]'}`}>
                      <Icon size={14} /> {t.label}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">{t.desc}</div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* ── Options (same set as single-tag printing) ────────────
              Both groups are always visible so the panel mirrors the
              single-tag printer. Each group is active only where it has an
              effect: Raw material + Pieces apply to Full Tag; QR size applies
              to QR only / QR + Image. The inactive group is dimmed. */}
          <section className="space-y-3">
            {/* Raw material + Pieces (Full Tag) */}
            <div
              className={`flex flex-wrap items-center gap-x-6 gap-y-2 transition-opacity ${mode === 'full' ? '' : 'opacity-45'}`}
              aria-disabled={mode !== 'full'}
            >
              <span className="text-[10px] uppercase tracking-wide text-slate-400 w-full -mb-1">Full Tag options</span>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-500">Raw material:</span>
                <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="batch-rm-mode">
                  <button
                    type="button"
                    disabled={mode !== 'full'}
                    onClick={() => setRmMode('expanded')}
                    aria-pressed={rmMode === 'expanded'}
                    data-testid="batch-rm-expanded"
                    className={`px-2.5 py-1 text-[11px] transition-colors disabled:cursor-not-allowed ${rmMode === 'expanded' ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    Expanded
                  </button>
                  <button
                    type="button"
                    disabled={mode !== 'full'}
                    onClick={() => setRmMode('collapsed')}
                    aria-pressed={rmMode === 'collapsed'}
                    data-testid="batch-rm-collapsed"
                    className={`px-2.5 py-1 text-[11px] border-l border-slate-200 transition-colors disabled:cursor-not-allowed ${rmMode === 'collapsed' ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    Collapsed
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-500">Pieces:</span>
                <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="batch-pieces">
                  <button
                    type="button"
                    disabled={mode !== 'full'}
                    onClick={() => setShowPieces(true)}
                    aria-pressed={showPieces}
                    data-testid="batch-pieces-on"
                    className={`px-2.5 py-1 text-[11px] transition-colors disabled:cursor-not-allowed ${showPieces ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    With pieces
                  </button>
                  <button
                    type="button"
                    disabled={mode !== 'full'}
                    onClick={() => setShowPieces(false)}
                    aria-pressed={!showPieces}
                    data-testid="batch-pieces-off"
                    className={`px-2.5 py-1 text-[11px] border-l border-slate-200 transition-colors disabled:cursor-not-allowed ${!showPieces ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    Without pieces
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-500">QR on tag:</span>
                <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="batch-tag-qr">
                  <button
                    type="button"
                    disabled={mode !== 'full'}
                    onClick={() => setShowTagQr(true)}
                    aria-pressed={showTagQr}
                    data-testid="batch-tag-qr-on"
                    className={`px-2.5 py-1 text-[11px] transition-colors disabled:cursor-not-allowed ${showTagQr ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    With QR
                  </button>
                  <button
                    type="button"
                    disabled={mode !== 'full'}
                    onClick={() => setShowTagQr(false)}
                    aria-pressed={!showTagQr}
                    data-testid="batch-tag-qr-off"
                    className={`px-2.5 py-1 text-[11px] border-l border-slate-200 transition-colors disabled:cursor-not-allowed ${!showTagQr ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    Without QR
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-500">Tag size:</span>
                <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="batch-tag-size">
                  {TAG_SIZES.map((z, i) => (
                    <button
                      key={z.id}
                      type="button"
                      disabled={mode !== 'full'}
                      onClick={() => { setSizeId(z.id); setCustomOn(false); }}
                      aria-pressed={!customOn && sizeId === z.id}
                      data-testid={`batch-tag-size-${z.id}`}
                      className={`px-2.5 py-1 text-[11px] transition-colors disabled:cursor-not-allowed ${i > 0 ? 'border-l border-slate-200' : ''} ${!customOn && sizeId === z.id ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                    >
                      {z.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={mode !== 'full'}
                    onClick={() => setCustomOn(true)}
                    aria-pressed={customOn}
                    data-testid="batch-tag-size-custom"
                    className={`px-2.5 py-1 text-[11px] border-l border-slate-200 transition-colors disabled:cursor-not-allowed ${customOn ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    Custom (mm)
                  </button>
                </div>
                {customOn ? (
                  <div className="flex items-center gap-1.5" data-testid="batch-custom-mm">
                    <input
                      type="number" min="5" max="300" value={customW}
                      disabled={mode !== 'full'}
                      onChange={(e) => setCustomW(Math.max(0, Number(e.target.value) || 0))}
                      data-testid="batch-custom-w"
                      className="w-14 h-7 rounded-md border border-slate-200 px-2 text-[11px] font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--jewellery-icon)] disabled:cursor-not-allowed"
                      aria-label="Label width in mm"
                    />
                    <span className="text-[11px] text-slate-400">×</span>
                    <input
                      type="number" min="5" max="300" value={customH}
                      disabled={mode !== 'full'}
                      onChange={(e) => setCustomH(Math.max(0, Number(e.target.value) || 0))}
                      data-testid="batch-custom-h"
                      className="w-14 h-7 rounded-md border border-slate-200 px-2 text-[11px] font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--jewellery-icon)] disabled:cursor-not-allowed"
                      aria-label="Label height in mm"
                    />
                    <span className="text-[11px] text-slate-400">mm</span>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Custom-size fit summary (auto-layout per tag) */}
            {customActive && orderedBatch.length > 0 ? (
              <div
                data-testid="batch-custom-fit-summary"
                className={`rounded-md border px-3 py-2 text-[12px] flex items-start gap-2 ${nonFitIds.length === 0 ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-300 bg-amber-50 text-amber-800'}`}
              >
                {nonFitIds.length === 0 ? (
                  <span>All {orderedBatch.length} tag(s) fit in <strong>{customW}×{customH} mm</strong>. Each tag auto-picks its best layout.</span>
                ) : (
                  <>
                    <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>{nonFitIds.length}</strong> of {orderedBatch.length} tag(s) won&apos;t fit in <strong>{customW}×{customH} mm</strong> at a readable size. When you print you&apos;ll choose to <strong>skip</strong> them or <strong>print best-effort</strong>.
                    </span>
                  </>
                )}
              </div>
            ) : null}

            {/* QR size (QR only / QR + Image) */}
            <div
              className={`flex items-center gap-2 flex-wrap transition-opacity ${isQrMode ? '' : 'opacity-45'}`}
              aria-disabled={!isQrMode}
            >
              <span className="text-[10px] uppercase tracking-wide text-slate-400 w-full -mb-1">QR only / QR + Image options</span>
              <span className="text-[11px] text-slate-500">QR size:</span>
              <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="batch-qr-size">
                {QR_LABEL_SIZES.map((q, i) => (
                  <button
                    key={q.id}
                    type="button"
                    disabled={!isQrMode}
                    onClick={() => setQrSizeId(q.id)}
                    aria-pressed={qrSizeId === q.id}
                    data-testid={`batch-qr-size-${q.id}`}
                    className={`px-2.5 py-1 text-[11px] transition-colors disabled:cursor-not-allowed ${i > 0 ? 'border-l border-slate-200' : ''} ${qrSizeId === q.id ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    {q.label}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* ── Per-tag layout override (Full Tag, preset sizes only) ──────
              Hidden for Custom (mm) because auto-fit chooses each tag's
              layout to fill the exact label size. */}
          {mode === 'full' && !customOn && ids.length > 0 ? (
            <section className="space-y-2" data-testid="batch-pertag-layout">
              <span className="text-[10px] uppercase tracking-wide text-slate-400">Layout per tag (optional — default Auto picks the best fit)</span>
              <div className="rounded-md border border-slate-200 divide-y divide-slate-100 max-h-44 overflow-y-auto">
                {ids.map((id) => {
                  const it = labelById.get(id);
                  const cur = layoutOverrideById[id] || 'auto';
                  return (
                    <div key={id} className="flex items-center justify-between gap-2 px-2.5 py-1.5" data-testid={`batch-pertag-row-${id}`}>
                      <span className="font-mono text-[11px] text-emerald-700 truncate min-w-0">{it?.jewellery_no || id.slice(0, 6)}</span>
                      <div className="inline-flex rounded-md border border-slate-200 overflow-hidden flex-shrink-0">
                        {BATCH_LAYOUT_OPTIONS.map((o, i) => (
                          <button
                            key={o.id}
                            type="button"
                            onClick={() => setLayoutOverrideById((prev) => ({ ...prev, [id]: o.id }))}
                            aria-pressed={cur === o.id}
                            data-testid={`batch-pertag-${id}-${o.id}`}
                            className={`px-2 py-0.5 text-[10px] transition-colors ${i > 0 ? 'border-l border-slate-200' : ''} ${cur === o.id ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                          >
                            {o.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ) : null}
          <section className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <ArrowDownUp size={13} className="text-slate-400" />
              <span className="text-[11px] text-slate-500">Order:</span>
              <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="batch-sort">
                {SORT_OPTIONS.map((s, i) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSortKey(s.id)}
                    aria-pressed={sortKey === s.id}
                    data-testid={`batch-sort-${s.id}`}
                    className={`px-2.5 py-1 text-[11px] transition-colors ${i > 0 ? 'border-l border-slate-200' : ''} ${sortKey === s.id ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => (preview ? setPreview(false) : runAction('preview'))}
              disabled={busy || ids.length === 0}
              className="h-8 ml-auto"
              data-testid="batch-preview-toggle"
            >
              {preview ? (<><EyeOff size={13} className="mr-1.5" /> Hide preview</>) : (<><Eye size={13} className="mr-1.5" /> Preview sheet</>)}
            </Button>
          </section>

          {/* ── Sheet preview (scaled A4) ───────────────────────────── */}
          {preview && orderedBatch.length > 0 ? (
            <section
              className="rounded-md border border-slate-200 bg-slate-100 p-2 overflow-auto"
              style={{ maxHeight: 340 }}
              data-testid="batch-preview-panel"
            >
              <div style={{ zoom: 0.5 }}>
                <div style={{ width: 760, margin: '0 auto', background: '#fff', padding: 10, boxShadow: '0 1px 4px rgba(15,23,42,0.12)' }}>
                  {orderedBatch.map((d) => renderBatchItem(d, true))}
                </div>
              </div>
            </section>
          ) : null}

          <p className="text-[11px] text-slate-500 leading-snug">
            Tags flow onto A4, multiple per page, and are never split across a page break.
            Full tags pick the best layout for each piece automatically. The PDF prints
            identically on any printer.
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={busy}
            data-testid="batch-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => runAction('pdf')}
            disabled={busy || ids.length === 0}
            className="border-[var(--jewellery-border)] text-[var(--jewellery-icon)] hover:bg-[var(--jewellery-bg)]"
            data-testid="batch-download-pdf"
          >
            {busy && busyLabel.startsWith('Building') ? (
              <><Loader2 size={15} className="mr-1.5 animate-spin" /> Building…</>
            ) : (
              <><Download size={15} className="mr-1.5" /> Download PDF</>
            )}
          </Button>
          <Button
            onClick={() => runAction('print')}
            disabled={busy || ids.length === 0}
            className="bg-[var(--ink-navy)] hover:bg-[var(--ink-navy-2)] text-white"
            data-testid="batch-print-confirm"
          >
            {busy && busyLabel === 'Preparing…' ? (
              <><Loader2 size={15} className="mr-1.5 animate-spin" /> Preparing…</>
            ) : (
              <><Printer size={15} className="mr-1.5" /> Print {ids.length || ''} {ids.length === 1 ? 'tag' : 'tags'}</>
            )}
          </Button>
        </DialogFooter>

        {/* Off-screen print sheet — rendered from the fetched batch data using
            the exact single-tag renderer so output matches the preview. In
            custom mode this uses the per-tag auto-fit layout/scale and the
            render list already honours a chosen Skip / Best-effort. */}
        <div style={{ position: 'absolute', left: -99999, top: 0, width: 734 }} aria-hidden="true">
          <div ref={offscreenRef}>
            {renderList.map((d) => renderBatchItem(d, false))}
          </div>
        </div>

        {/* Off-screen fit-measurement nodes — only when a custom size is
            active. Each tag renders every layout candidate at natural size so
            the fit engine can pick the best layout/scale per tag. */}
        {customActive && orderedBatch.length > 0 ? (
          <div style={{ position: 'absolute', left: -99999, top: 0 }} aria-hidden="true" data-testid="batch-measure">
            {orderedBatch.map((d) => (
              FIT_CANDIDATES.map((c, i) => (
                <TagMeasureNode
                  key={`${d.id}::${i}`}
                  data={d}
                  candidate={c}
                  rmMode={rmMode}
                  showPieces={showPieces}
                  showQr={showTagQr}
                  nodeRef={(el) => { measureRefs.current[`${d.id}::${i}`] = el; }}
                />
              ))
            ))}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>

      {/* ── Fit warning dialog (custom size, some tags don't fit) ───────── */}
      <Dialog
        open={warnOpen}
        onOpenChange={(o) => { if (!o) { setWarnOpen(false); pendingActionRef.current = null; } }}
      >
        <DialogContent className="max-w-md" data-testid="batch-fit-warning-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-600" />
              Some tags don&apos;t fit
            </DialogTitle>
            <DialogDescription>
              {nonFitIds.length} of {orderedBatch.length} selected tag(s) can&apos;t fit all their
              data in <strong>{customW}×{customH} mm</strong> at a readable size. Choose how to handle them.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-md border border-amber-200 bg-amber-50/60 p-2 max-h-40 overflow-y-auto" data-testid="batch-nonfit-list">
            <div className="flex flex-wrap gap-1.5">
              {nonFitIds.map((id) => (
                <span
                  key={id}
                  className="inline-flex items-center rounded-full border border-amber-300 bg-white px-2 py-0.5 text-[11px] font-mono text-amber-800"
                  data-testid={`batch-nonfit-${id}`}
                >
                  {labelById.get(id)?.jewellery_no || id.slice(0, 6)}
                </span>
              ))}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              variant="ghost"
              onClick={() => { setWarnOpen(false); pendingActionRef.current = null; }}
              data-testid="batch-fit-cancel"
            >
              Cancel
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => resolveWarn('skip')}
                data-testid="batch-fit-skip"
                className="border-[var(--jewellery-border)] text-[var(--jewellery-icon)] hover:bg-[var(--jewellery-bg)]"
              >
                Skip non-fitting
              </Button>
              <Button
                onClick={() => resolveWarn('best')}
                data-testid="batch-fit-best"
                className="bg-[var(--ink-navy)] hover:bg-[var(--ink-navy-2)] text-white"
              >
                Print anyway (best effort)
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default JewelleryBatchPrintDialog;
